/**
 * PropertyEditPage - Form for editing an existing property.
 *
 * Loads the property data and renders PropertyForm in edit mode.
 */

import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'

import { useAuth } from '@/common/hooks/useAuth'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { Icon } from '@/common/components/icon'

import { getProperty, PropertySelect } from '@/features/properties/api/properties'
import type { PropertyResponse } from '@/features/properties/api/properties'
import { PropertyForm } from '@/features/properties/components/PropertyForm'
import * as styles from './edit.css'

export const Route = createFileRoute({
  component: PropertyEditPage,
})

function PropertyEditPage() {
  const { id } = useParams({ strict: false })
  const navigate = useNavigate()
  const { token } = useAuth()

  // Fetch property data with full address (lat/lon) and media for edit form
  const {
    data: property,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['property', id, 'edit'],
    queryFn: () => getProperty(token!, id!, { select: PropertySelect.edit }),
    enabled: !!token && !!id,
  })

  const handleSuccess = (updatedProperty: PropertyResponse) => {
    navigate({ to: '/properties/$id', params: { id: updatedProperty.id } })
  }

  const handleCancel = () => {
    if (id) {
      navigate({ to: '/properties/$id', params: { id } })
    } else {
      navigate({ to: '/properties' })
    }
  }

  const handleDelete = () => {
    navigate({ to: '/properties' })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error state
  if (error || !property) {
    return (
      <div className={styles.container}>
        <Link to="/properties" className={styles.backLink}>
          <Icon name="arrow_back" size={16} />
          Back to Properties
        </Link>
        <div className={styles.errorContainer}>
          <Text as="h2" variant="bodyLg" weight="bold">
            Property Not Found
          </Text>
          <Text variant="body" color="muted">
            The property you're trying to edit doesn't exist or you don't have
            permission to edit it.
          </Text>
        </div>
      </div>
    )
  }

  return (
    <PropertyForm
      mode="edit"
      initialData={property}
      onSuccess={handleSuccess}
      onCancel={handleCancel}
      onDelete={handleDelete}
    />
  )
}
